import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import get from 'lodash/get';
import { Box, Link, Text } from '@nerdwallet/currency';
import BackgroundColor from '../background-color/background-color';
import './ticker-tape.less';
import getTextColor from '../../lib/text-color-config';
import {
  boolCheckboxSpec,
  numberInputSpec,
  stringInputSpec,
  stringSelectSpec,
  stringTextAreaSpec,
} from '../../lib/editing-configs';
import parseStyleFormatting from '../../lib/parse-style-formatting';

const renderTickerTapeText = (text, repetitions, textColorStyle, iconUrl) => {
  return [...new Array(repetitions)].map((_, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <Box key={index} className="marquee--content__item" style={textColorStyle}>
      {iconUrl && <img src={iconUrl} alt="ticker-tape-icon" />}
      <Text component="p">{text}</Text>
      {iconUrl && <img src={iconUrl} alt="ticker-tape-icon" />}
    </Box>
  ));
};

const renderContent = ({
  animationDuration,
  startScrollingFrom,
  text,
  textRepetitions,
  colorStyle,
  iconImageUrl,
}) => (
  <Box
    style={{ animationDuration }}
    className={classNames([
      'marquee--content',
      {
        'from-left': startScrollingFrom === 'left',
      },
    ])}
  >
    {renderTickerTapeText(
      parseStyleFormatting(text),
      textRepetitions,
      colorStyle,
      iconImageUrl
    )}
  </Box>
);

const TickerTape = ({
  bgOptions,
  text,
  textColorOptions,
  iconImageUrl,
  startScrollingFrom,
  scrollingSpeed,
  textRepetitions,
  enableScrolling,
  linkUrl,
}) => {
  const { textColor } = getTextColor(textColorOptions);
  const colorStyle = { color: textColor };
  const animationDuration = enableScrolling ? scrollingSpeed : null;

  return (
    <BackgroundColor bgOptions={bgOptions}>
      {(backgroundColor) =>
        linkUrl ? (
          <Link
            href={linkUrl}
            target="_blank"
            className="marquee"
            style={{ backgroundColor }}
          >
            {renderContent({
              animationDuration,
              startScrollingFrom,
              text,
              colorStyle,
              iconImageUrl,
              textRepetitions,
            })}
          </Link>
        ) : (
          <Box className="marquee" style={{ backgroundColor }}>
            {renderContent({
              animationDuration,
              startScrollingFrom,
              text,
              colorStyle,
              iconImageUrl,
              textRepetitions,
            })}
          </Box>
        )
      }
    </BackgroundColor>
  );
};

TickerTape.propTypes = {
  ...BackgroundColor.propTypes,
  text: PropTypes.string,
  iconImageUrl: PropTypes.string,
  linkUrl: PropTypes.string,
  startScrollingFrom: PropTypes.oneOf(['left', 'right']),
  textRepetitions: PropTypes.number,
  enableScrolling: PropTypes.bool,
};

TickerTape.defaultProps = {
  ...BackgroundColor.defaultProps,
  text: '',
  iconImageUrl: '',
  linkUrl: '',
  startScrollingFrom: 'right',
  scrollingSpeed: '10s',
  textRepetitions: 10,
  enableScrolling: false,
};

TickerTape.getEditingConfig = (componentDocument) => {
  const scrollingConfigOptions =
    get(componentDocument, 'props.enableScrolling', false) === true
      ? [
          boolCheckboxSpec('enableScrolling', 'Enable scrolling?'),
          stringSelectSpec(
            'startScrollingFrom',
            'Scrolling Direction',
            [
              { label: 'Right to left', value: 'right' },
              { label: 'Left to right', value: 'left' },
            ],
            {
              help: 'Defaults to "Right to left"',
            }
          ),
          stringSelectSpec(
            'scrollingSpeed',
            'Scrolling Speed',
            [
              { label: 'Slow', value: '10s' },
              { label: 'Fast', value: '5s' },
              { label: 'Faster', value: '3s' },
            ],
            {
              help: 'Defaults to "Slow"',
            }
          ),
          numberInputSpec('textRepetitions', 'Text repetitions', {
            help: 'Repetitions of the text along the page. Defaults to "10"',
          }),
        ]
      : [boolCheckboxSpec('enableScrolling', 'Enable scrolling?')];
  return [
    ...BackgroundColor.getEditingConfig(componentDocument),
    stringTextAreaSpec('text', 'Text', {
      help: 'For GothamBlack font, surround title text with | like |this|',
    }),
    stringInputSpec('iconImageUrl', 'Icon image URL'),
    stringInputSpec('linkUrl', 'Link URL'),
    ...getTextColor.getEditingConfig(
      componentDocument,
      'textColorOptions',
      'Text Color'
    ),
    ...scrollingConfigOptions,
  ];
};

export default TickerTape;
